import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import SelectBox from '@adsk/alloy-select-box';
import FormField from '@adsk/alloy-react-form-field';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  AutocompleteControl,
  AutocompleteMenu,
  MenuItem,
  useAutocomplete,
} from '@adsk/alloy-react-dropdown';
import Footer, { ClearButton } from '@adsk/alloy-react-footer';
import Theme from '@adsk/alloy-react-theme';
import Divider from '@adsk/alloy-react-divider';
import Tooltip from '@adsk/alloy-react-tooltip';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import {
  COMPONENT_VARIANTS,
  FLAG_NAMES,
  removeExtraBlankSpaces,
} from '@adsk/acs-admin-ui-components-helpers';
import { useAdminFeatureFlag } from '../../../AdminLaunchDarklyProvider';
import CompanyFieldFooter from '../../../CompanyFieldFooter';
import {
  fieldStyle,
  roleAccessTitleStyle,
  roleAccessTitleTextStyle,
} from './styles';

const EditableMemberInfo = ({
  canCreateCompanies,
  companies,
  companySearchInput,
  companyCreateError,
  roles,
  isEmsAccount,
  isFetchingCompanies,
  isFetchingRoles,
  onChangeCompanyInput,
  onChangeRoleInput,
  onCompanyChange,
  onCreateCompany,
  onRoleChange,
  roleSearchInput,
  selectedCompany,
  selectedRole,
  variant,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const {
    ADMIN_FLAGS: { ENABLE_PROJECT_ROLES_PRODUCTS },
  } = FLAG_NAMES;
  const { value: enableProjectRolesProducts } = useAdminFeatureFlag(
    ENABLE_PROJECT_ROLES_PRODUCTS
  );
  const orderedCompanies = useMemo(() => {
    return selectedCompany
      ? [
          selectedCompany,
          ...companies.filter((company) => company.id !== selectedCompany.id),
        ]
      : companies;
  }, [companies, selectedCompany]);

  const itemToString = useCallback((item) => item?.name || '', []);
  const getFilteredItems = useCallback(
    ({ inputValue, allItems }) => {
      if (!inputValue) {
        return allItems;
      }
      if (inputValue.trim().length === 0 && inputValue.length > 1) {
        return [];
      }
      const search = removeExtraBlankSpaces(inputValue).toLowerCase();
      return allItems.filter((item) => {
        const name = removeExtraBlankSpaces(itemToString(item)).toLowerCase();
        return name.includes(search);
      });
    },
    [itemToString]
  );

  const {
    closeMenu,
    controlProps,
    menuProps: allMenuProps,
  } = useAutocomplete({
    getFilteredItems,
    id: 'MemberProfilePanel_Company_Autocomplete',
    items: orderedCompanies,
    itemToString,
    onInputValueChange: ({ inputValue }) => {
      if (inputValue && inputValue !== selectedCompany?.name) {
        onChangeCompanyInput(inputValue);
      }
    },
    onIsOpenChange: ({ isOpen: isAutocompleteOpen }) => {
      if (!isAutocompleteOpen) {
        onChangeCompanyInput('');
      }
    },
    onSelectedItemChange: ({ selectedItem: selected }) => {
      onCompanyChange(selected);
    },
    selectedItem: selectedCompany,
  });

  const { menuItems, getItemProps, ...menuProps } = allMenuProps;

  return (
    <>
      {!isEmsAccount && (
        <div style={fieldStyle}>
          <FormField
            error={companyCreateError ? T.translate('ADD_COMPANY_ERROR') : ''}
            data-testid="Company_SelectBox"
            label={T.translate('COMPANY_LABEL')}
            labelVariant={FormField.LABEL_VARIANTS.TOP}
          >
            <AutocompleteControl
              {...controlProps}
              placeholder={T.translate('COMPANY_PLACEHOLDER')}
            />
            <AutocompleteMenu
              {...menuProps}
              isLoading={isFetchingCompanies}
              renderFooter={() => (
                <div>
                  <CompanyFieldFooter
                    canCreateCompanies={canCreateCompanies}
                    companies={companies}
                    inputValue={companySearchInput}
                    onCreateCompany={() => {
                      onCreateCompany();
                      closeMenu();
                    }}
                    style={{
                      borderTop: `1px solid ${Theme.colors.charcoal200}`,
                    }}
                  />
                  <Footer>
                    <ClearButton
                      onClick={() => {
                        onCompanyChange(null);
                        closeMenu();
                      }}
                    />
                  </Footer>
                </div>
              )}
              style={{ maxHeight: '200px' }}
            >
              {menuItems.map((item, index) => (
                <MenuItem
                  key={index}
                  {...getItemProps?.({ item })}
                  data-testid={`Dropdown__MenuItem--${index}`}
                  text={item.name}
                />
              ))}
            </AutocompleteMenu>
          </FormField>
        </div>
      )}
      {variant === COMPONENT_VARIANTS.PROJECT && enableProjectRolesProducts && (
        <>
          <div style={roleAccessTitleStyle}>
            <span style={roleAccessTitleTextStyle}>
              {T.translate('ROLE_ACCESS_TITLE')}
            </span>
            <Tooltip
              delayHide={1000}
              content={T.translate('ROLE_ACCESS_TITLE_TOOLTIP')}
              shouldWrapChildren={false}
              placement={Tooltip.PLACEMENTS.TOP}
            >
              <AlertInformationIcon
                data-testid="role-level-access-tooltip-icon"
                size={16}
              />
            </Tooltip>
          </div>
          <Divider />
        </>
      )}
      <div style={fieldStyle}>
        <FormField
          label={T.translate('ROLE_LABEL')}
          labelVariant={FormField.LABEL_VARIANTS.TOP}
        >
          <SelectBox
            clearable
            data-testid="Role_SelectBox"
            ignoreCase
            inputValue={roleSearchInput}
            isLoading={isFetchingRoles}
            labelKey="name"
            maxMenuHeight={150}
            multiple={variant === COMPONENT_VARIANTS.PROJECT}
            onChange={onRoleChange}
            onInputChange={onChangeRoleInput}
            options={roles.filter((role) => role.status === 'active')}
            placeholder={T.translate('ROLE_PLACEHOLDER')}
            searchable
            style={{ paddingRight: '10px' }}
            value={selectedRole}
            valueKey="id"
          />
        </FormField>
      </div>
    </>
  );
};

EditableMemberInfo.propTypes = {
  canCreateCompanies: PropTypes.bool.isRequired,
  companies: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  companyCreateError: PropTypes.bool.isRequired,
  companySearchInput: PropTypes.string,
  isEmsAccount: PropTypes.bool,
  isFetchingCompanies: PropTypes.bool.isRequired,
  isFetchingRoles: PropTypes.bool.isRequired,
  onChangeCompanyInput: PropTypes.func,
  onChangeRoleInput: PropTypes.func,
  onCompanyChange: PropTypes.func.isRequired,
  onCreateCompany: PropTypes.func,
  onRoleChange: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  roleSearchInput: PropTypes.string,
  selectedCompany: PropTypes.objectOf(PropTypes.string),
  selectedRole: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  variant: PropTypes.oneOf(Object.values(COMPONENT_VARIANTS)).isRequired,
};

EditableMemberInfo.defaultProps = {
  companies: [],
  companySearchInput: undefined,
  isEmsAccount: false,
  onChangeCompanyInput: undefined,
  onChangeRoleInput: undefined,
  onCreateCompany: undefined,
  roles: [],
  roleSearchInput: undefined,
  selectedCompany: null,
  selectedRole: null,
};

export default EditableMemberInfo;
